import React, { FC } from 'react';

import { DateTime } from 'luxon';

import {
  IAdminBookedPharmacistDetails,
  IAdminRequestedDetailsData,
  ILongContractRequestResponse,
  IPharmacistDetails,
  IResume,
  ProfessionalReplacementTypes,
} from '@pharmaplan/common';

import { ReplacementLabels } from '../../../../helpers/Constants';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { setDialog } from '../../../../reducers/dialogReducer';
import ExperienceContent from './ExperienceContent';
import PDFViewer from '../../../common/PDFViewer';
import strings from '../../../../localization';
import ServiceCard from '../../ServiceCard';
import DocViewer from '../../../common/DocViewer';

const currentYear = DateTime.now().year;

interface IProfessionalExperience {
  successAction: string;
  resume: IResume;
  details:
    | IPharmacistDetails
    | ILongContractRequestResponse
    | IAdminBookedPharmacistDetails
    | IAdminRequestedDetailsData;
}

interface IProfileContentConfig {
  key: number | string;
  label: string;
  value: string | number | React.ReactNode;
  customOpen?: () => void;
  isUrl?: boolean;
}

const ProfessionalExperience: FC<IProfessionalExperience> = ({
  successAction,
  details,
  resume,
}) => {
  const dispatch = useAppDispatch();

  const {
    diploma,
    instituteName,
    techGraduationYear,
    language,
    graduationYear = 0,
    certificateFileName,
    replacementType,
  } = details ?? {};

  const isTechnician = replacementType === ProfessionalReplacementTypes.technician;
  const viewPDF = (source: string) =>
    <PDFViewer source={source} />;

  const viewImage = (source: string) =>
    <img src={source} alt="1" />;

  const viewDoc = (source: string) =>
    <DocViewer source={source} />;

  const viewDocument = (url: string, fileName: string) =>
    () => {
      const re = /(?:\.([^.]+))?$/;

      const extension = re?.exec?.(fileName)?.[1] ?? '';
      const isDoc = ['docx', 'doc'].includes(extension);

      const renders = () => {
        if (isDoc) {
          return viewDoc(url);
        }
        if (['pdf'].includes(extension)) {
          return viewPDF(url);
        }
        if (['jpg', 'jpeg', 'png'].includes(extension)) {
          return viewImage(url);
        }

        return viewImage(url);
      };

      dispatch(
        setDialog({
          showCloseButton: true,
          Component: renders(),
          maxWidth: isDoc ? 'md' : 'sm',
          heading: {
            title: '',
          },
        }),
      );
    };

  const config = [
    isTechnician && { key: 0, value: diploma, label: strings.diploma },
    isTechnician && {
      key: 1,
      value: instituteName,
      label: strings.educationalInstitute,
    },
    isTechnician && {
      key: 2,
      value: techGraduationYear,
      label: strings.graduationYear,
    },
    resume?.fileName && {
      key: 3,
      value: resume?.fileName,
      customOpen: viewDocument(resume?.url ?? '', resume?.fileName),
      isUrl: true,
      label: strings.formatString(
        strings.userCVFormat,
        ReplacementLabels()[
          replacementType ?? ProfessionalReplacementTypes.pharmacist
        ],
      ),
    },
    isTechnician && certificateFileName && {
      key: 4,
      value: certificateFileName,
      customOpen: viewDocument(
        details?.certificateUrl ?? '',
        certificateFileName ?? '',
      ),
      isUrl: true,
      label: strings.diplomaCertificate,
    },
    { key: 5, value: language?.name, label: strings.language },
    {
      key: 6,
      value: currentYear - (graduationYear as number),
      label: strings.yearsOfExperience,
    },
  ].filter(Boolean) as Array<IProfileContentConfig>;

  return (
    <ServiceCard
      title={strings.experience}
      content={
        <ExperienceContent successAction={successAction} content={config} />
      }
    />
  );
};

export default ProfessionalExperience;
